import React, { forwardRef, ReactNode } from 'react'
import { Icon, IconProps } from '../types'

export type MakeIconArgs = {
  displayName?: string
  viewBox?: number
  content: ReactNode
  viewBoxWidth?: number
  viewBoxHeight?: number
  smallAlternative?: Icon
}

const MAX_PX_FOR_SMALL_ALTERNATIVE = 12

const PX_PER_EM_IN_NORMAL_BODY_TEXT = 16

// Note that this is inherently inaccurate: for sizes in em, we can't know the font-size in the CSS context.
// We therefore guess that we're in normal body text, and that the font size is 16px.
// To make this truly accurate, we should move the decision to a CSS @container query.
function toEstimatedPx(size: string | number): number {
  if (typeof size === 'number') {
    return size
  }

  if (typeof size === 'string' && size.match(/^[0-9.]+em$/)) {
    return parseFloat(size) * PX_PER_EM_IN_NORMAL_BODY_TEXT
  }

  return 16
}

// Deprecated -- Use IconBase instead, or just plain SVG
export const makeIcon = ({
  displayName = 'Icon',
  viewBox = 16,
  content,
  viewBoxWidth = viewBox,
  viewBoxHeight = viewBox,
  smallAlternative: SmallAlternative,
}: MakeIconArgs): Icon => {
  const IconComponent = forwardRef<SVGSVGElement, IconProps>(
    (iconProps, ref) => {
      const { size = '1em', style, color, hGradient, ...otherProps } = iconProps

      // Limitation: for the small alternative to be used, size must be specified as either:
      // - a number, interpreted as number of pixels, or
      // - a string ending in "em", and <= 0.75em
      const preferSmallAlternative =
        toEstimatedPx(size) <= MAX_PX_FOR_SMALL_ALTERNATIVE

      if (preferSmallAlternative && SmallAlternative) {
        return <SmallAlternative {...iconProps} ref={ref} />
      }
      return (
        <svg
          ref={ref}
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          width={size}
          height={size}
          style={{ verticalAlign: 'middle', ...style }}
          preserveAspectRatio="xMidYMid meet"
          {...otherProps}
        >
          {hGradient && (
            <linearGradient
              x1="100%"
              y1="100%"
              x2="0%"
              y2="0%"
              id="linearGradient-1"
            >
              <stop stopColor={hGradient.startColor} offset="0%" />
              <stop stopColor={hGradient.endColor} offset="100%" />
            </linearGradient>
          )}
          <g
            fill={
              hGradient ? 'url(#linearGradient-1)' : color || 'currentColor'
            }
          >
            {content}
          </g>
        </svg>
      )
    }
  )

  IconComponent.displayName = displayName
  return React.memo(IconComponent) as Icon
}
